<template>
    <div class="property-reviews">
        <v-row class="mb-4">
            <v-col cols="12" sm="8">
                <h3>Reviews</h3>
            </v-col>
            <v-col cols="12" sm="4" class="d-flex justify-sm-end align-center">
                <v-btn
                    v-if="canWriteReview"
                    color="primary"
                    depressed
                    class="btn-edurent"
                    @click="openReviewForm"
                >
                    <v-icon left>mdi-pencil</v-icon>
                    Write a Review
                </v-btn>
            </v-col>
        </v-row>

        <v-row v-if="reviewStats && reviewStats.review_count > 0">
            <v-col cols="12">
                <ReviewStats :stats="reviewStats" :reviews="reviews" />
            </v-col>
        </v-row>

        <v-row v-if="reviews.length > 0" class="mt-4">
            <v-col cols="12" sm="6" md="4">
                <v-select
                    v-model="sortBy"
                    :items="sortOptions"
                    label="Sort by"
                    outlined
                    dense
                    hide-details
                    @change="handleSortChange"
                />
            </v-col>
        </v-row>

        <v-row v-if="isLoading">
            <v-col cols="12">
                <v-skeleton-loader type="article" class="mb-4" />
                <v-skeleton-loader type="article" class="mb-4" />
            </v-col>
        </v-row>

        <v-row v-else-if="reviews.length > 0">
            <v-col cols="12">
                <ReviewCard
                    v-for="review in reviews"
                    :key="review.id"
                    :review="review"
                    @vote="handleVote"
                />
            </v-col>
        </v-row>

        <v-row v-else>
            <v-col cols="12" class="text-center py-8">
                <v-icon size="64" color="grey lighten-2">mdi-comment-text-outline</v-icon>
                <p class="grey--text mt-4">No reviews yet.</p>
                <p class="grey--text text-caption" v-if="!canWriteReview && isUserLoggedIn">
                    Only verified tenants who have lived at this property can write reviews.
                </p>
            </v-col>
        </v-row>

        <ReviewForm
            :dialog="showReviewFormModal"
            :property-name="propertyName"
            :property-id="propertyId"
        />
    </div>
</template>

<script>
import ReviewStats from './ReviewStats.vue';
import ReviewCard from './ReviewCard.vue';
import ReviewForm from './ReviewForm.vue';

export default {
    name: 'PropertyReviews',
    components: {
        ReviewStats,
        ReviewCard,
        ReviewForm
    },
    props: {
        propertyId: {
            type: [Number, String],
            required: true
        },
        propertyName: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            sortOptions: [
                { text: 'Most Helpful', value: 'most_helpful' },
                { text: 'Newest', value: 'newest' },
                { text: 'Highest Rated', value: 'highest_rated' },
                { text: 'Lowest Rated', value: 'lowest_rated' }
            ]
        }
    },
    computed: {
        reviews() {
            return this.$store.getters.getReviews;
        },
        reviewStats() {
            return this.$store.getters.getReviewStats;
        },
        eligibility() {
            return this.$store.getters.getReviewEligibility;
        },
        isLoading() {
            return this.$store.getters.isLoadingReviews;
        },
        showReviewFormModal() {
            return this.$store.getters.isReviewFormModalOpen;
        },
        isUserLoggedIn() {
            return this.$store.getters.isUserLoggedIn;
        },
        canWriteReview() {
            return this.isUserLoggedIn && this.eligibility && this.eligibility.can_review;
        },
        sortBy: {
            get() {
                return this.$store.getters.getReviewSortBy;
            },
            // eslint-disable-next-line no-unused-vars
            set(value) {
                // Handled by handleSortChange
            }
        }
    },
    methods: {
        openReviewForm() {
            this.$store.dispatch('setReviewFormModal', true);
        },
        handleSortChange(value) {
            this.$store.dispatch('setReviewSortBy', {
                sortBy: value,
                propertyId: this.propertyId
            });
        },
        handleVote({ reviewId }) {
            this.$store.dispatch('toggleHelpfulVote', {
                reviewId,
                propertyId: this.propertyId
            });
        },
        async loadReviews() {
            await Promise.all([
                this.$store.dispatch('fetchPropertyReviews', this.propertyId),
                this.$store.dispatch('fetchPropertyRating', this.propertyId)
            ]);

            if (this.isUserLoggedIn) {
                await this.$store.dispatch('checkReviewEligibility', this.propertyId);
            }
        }
    },
    async mounted() {
        await this.loadReviews();
    },
    watch: {
        propertyId: {
            handler() {
                this.loadReviews();
            }
        },
        isUserLoggedIn: {
            handler(newVal) {
                if (newVal) {
                    this.$store.dispatch('checkReviewEligibility', this.propertyId);
                }
            }
        }
    },
    beforeDestroy() {
        this.$store.dispatch('clearReviewsState');
    }
}
</script>

<style scoped>
.property-reviews h3 {
    font-size: 20px !important;
    font-weight: 700;
    color: #000;
}
</style>
